<script setup>
/**
 * FormControlPagesComponentId
 * Contextual component assignment picker for layout engine pages.
 * Displays as a plain text block for locked listing layouts, or opens a
 * select menu dropdown when page layouts are actively being configured.
 */
import { computed, watch, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeSelect from '../form-control-type/FormControlTypeSelect.vue';
import FormControlPlainText from "./FormControlPlainText.vue";

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Synchronous Computeds ---

const betaMode = computed(() => store.state.betaMode);

const componentsItems = computed(() => store.state.components?.items || []);

/**
 * Resolves the display header title for the associated component id safely,
 * providing a loading placeholder if state hydration arrays are still processing.
 */
const componentTitle = computed(() => {
    const targetComponentId = props.currentData?.componentId;
    if (props.currentData?.id && targetComponentId) {
        const match = componentsItems.value.find(o => o.id === targetComponentId);
        return match ? match.title : 'Loading component title...';
    }
    return '';
});

/**
 * Filter and map global component schemas specifically flagged for list representations.
 */
const options = computed(() => {
    if (props.currentData?.type === 'Listing') {
        return componentsItems.value
            .filter(o => o.listing)
            .map(o => ({
                value: o.id,
                text: o.title
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }
    return null;
});

// --- Structural Deep Watcher ---

/**
 * Contextual Layout Watcher: Listens closely to page type updates to manage form logic.
 * Drops component associations instantly if types switch away from list formats.
 */
watch(() => props.currentData, (newData) => {
    if (!newData || !props.field) return;

    if (newData.type === 'Listing') {
        props.field.visibility = '';
    } else {
        props.field.visibility = 'hidden';
        // Mutate the reactive state pointer cleanly
        newData.componentId = null;
    }
}, { deep: true, immediate: true });

// --- Lifecycle ---

onMounted(() => {
    if (props.field && props.currentData) {
        // Evaluate locked plain-text view criteria conditions
        props.field.readonly = props.currentData.type === 'Listing' && !!props.currentData.id && !betaMode.value;
    }
});
</script>

<template>
    <div class="form-control-pages-component-wrapper">

        <FormControlPlainText
            v-if="field.readonly"
            :value="componentTitle"
            :form-id="formId"
        />

        <FormControlTypeSelect
            v-else-if="options"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
            :options="options"
        />

    </div>
</template>